import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormGroupErrorMessageComponent, LoadingPanelService,
  noWhitespaceValidator, Utils, AlertService, AlertIcon } from 'lib-base';
import { ButtonModule } from 'primeng/button';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { IDepartamento, IMunicipio, IPais, ITipoIdentificacion, RegistrarUsuarioRequest } from '../../../../shared/interfaces/seguridad';
import { TipoIdentificacionService } from '../../../../services/tipoIdentificacion.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { CalendarModule } from 'primeng/calendar';
import { PaisService } from '../../../../services/pais.service';
import { DepartamentoService } from '../../../../services/departamento.service';
import { MunicipioService } from '../../../../services/municipio.service';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { PortalUsuarioService } from '../../../../services/portalUsuario.service';
import { AuthenticationManagerService } from '../../../../services/authentication-manager.service';
import {IconFieldModule} from "primeng/iconfield";
import {InputIconModule} from "primeng/inputicon";
import {ToastModule} from "primeng/toast";

@Component({
  selector: 'app-register-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FormGroupErrorMessageComponent, ButtonModule, InputTextModule,
    TranslateModule, DropdownModule, InputNumberModule, CalendarModule, FileUploadModule, IconFieldModule, InputIconModule, ToastModule
  ],
  templateUrl: './register-form.component.html',
  styleUrl: './register-form.component.scss'
})
export class RegisterFormComponent implements OnInit {
  form !: FormGroup;

  tipoIdentificaciones: ITipoIdentificacion[] = [];
  paises: IPais[] = [];
  departamentos: IDepartamento[] = [];
  municipios: IMunicipio[] = [];

  archivoCedula?: File = undefined;
  readonly uploadFileSize: number = 1024 * 1024
  invalidSize = false;
  requieredFile = false;

  private _translations: any = {};
  constructor(private _fb: FormBuilder, private _ti: TipoIdentificacionService, private _p: PaisService, private _d: DepartamentoService, private _m: MunicipioService,
    private _ms: MessageService, private _translate: TranslateService,
    private _pu: PortalUsuarioService, private _am: AuthenticationManagerService,
    private _lps: LoadingPanelService, private alertService: AlertService
  ) {
    this.configureForm();
  }

  ngOnInit() {
    this.translate();
    this.getTipoIdentificaciones();
    this.getPaises();
  }

  private async translate(): Promise<void> {
    this._translations = await firstValueFrom(forkJoin({
      completeCampos: this._translate.get('form.msg.complete-campos'),
      warnSummary: this._translate.get('form.msg.warn-summary'),
      registrarExito: this._translate.get('components.app-authentication-manager.register-form.registrar-exito'),
      aceptar: this._translate.get('form.button.aceptar'),
    }));
  }

  get isOtroPaisExpedicion() {
    return this.form.get('paisExpedicionId')?.value?.descripcion === "OTRO";
  }

  get isOtroDepartamentoExpedicion() {
    return this.form.get('departamentoExpedicionId')?.value?.descripcion === "OTRO";
  }

  get isOtroMunicipioExpedicion() {
    return this.form.get('municipioExpedicionId')?.value?.descripcion === "OTRO";
  }

  get isMunicipioExpedicionIdVisible() {
    if(this.isOtroPaisExpedicion){
      return false;
    }
    if(this.isOtroDepartamentoExpedicion){
      return false;
    }
    return true;
  }

  configureForm() {
    this.form = this._fb.group({
      primerNombre: [null, [Validators.required, Validators.maxLength(50), noWhitespaceValidator]],
      segundoNombre: [null, [Validators.maxLength(50)]],
      primerApellido: [null, [Validators.required, Validators.maxLength(50), noWhitespaceValidator]],
      segundoApellido: [null, [Validators.maxLength(50)]],
      telefono: [null, [Validators.required, Validators.maxLength(60), noWhitespaceValidator]],
      correoElectronico: [null, [Validators.required, Validators.email, Validators.maxLength(100), noWhitespaceValidator]],
      direccion: [null, [Validators.required, Validators.maxLength(200), noWhitespaceValidator]],
      tipoIdentificacionId: [null, Validators.required],
      identificacion: [null, [Validators.required, Validators.maxLength(20)]],
      paisExpedicionId: [null, [Validators.required]],
      departamentoExpedicionId: [null],
      municipioExpedicionId: [null],
      otroPaisExpedicion: [null, [Validators.maxLength(100)]],
      otroDepartamentoExpedicion: [null, [Validators.maxLength(100)]],
      otroMunicipioExpedicion: [null, [Validators.maxLength(100)]],
      fechaExpedicion: [null, [Validators.required]],
    });
  }
  //#region API Calls
  getTipoIdentificaciones() {
    this._ti.getTipoIdentificaciones().subscribe({
      next: (response: ITipoIdentificacion[]) => {
        this.tipoIdentificaciones = response;
      }
    });
  }

  getPaises() {
    this._p.getPaises().subscribe({
      next: (response: IPais[]) => {
        this.paises = response;
      }
    });
  }

  getDepartamentos() {
    const paisId = this.form.get('paisExpedicionId')?.value.id;
    this._d.getDepartamentos(paisId).subscribe({
      next: (response: IDepartamento[]) => {
        this.departamentos = response;
      }
    });
  }

  getMunicipios() {
    const paisId = this.form.get('paisExpedicionId')?.value.id;
    const departamentoId = this.form.get('departamentoExpedicionId')?.value.id;
    this._m.getMunicipios(paisId, departamentoId).subscribe({
      next: (response: IMunicipio[]) => {
        this.municipios = response;
      }
    });
  }

  registrarUsuario(request: RegistrarUsuarioRequest) {
    if (this.archivoCedula === undefined) {
      this.requieredFile = true;
      return;
    }
    this._lps.show(true);
    this._pu.registrarUsuario(request, this.archivoCedula).subscribe({
      next: (response: string) => {
        this._lps.hide();
         this.alertService.show({
          text: this._translations.registrarExito,
          icon: AlertIcon.SUCCESS,
          showAccept: true,
           textAccept: this._translations.aceptar
        }).subscribe(async (result) => {
          this._am.onMenuClose();
        });
      }
    });
  }
  //#endregion API Calls

  onPaisChange(event: DropdownChangeEvent) {
    this.form.get('departamentoExpedicionId')?.setValue(null);
    this.form.get('municipioExpedicionId')?.setValue(null);
    this.form.get('otroPaisExpedicion')?.setValue('');
    this.form.get('otroDepartamentoExpedicion')?.setValue('');
    this.form.get('otroMunicipioExpedicion')?.setValue('');
    this.municipios = [];
    this.getDepartamentos();
  }

  onDepartamentoChange(event: DropdownChangeEvent) {
    this.form.get('municipioExpedicionId')?.setValue(null);
    this.form.get('otroPaisExpedicion')?.setValue('');
    this.form.get('otroDepartamentoExpedicion')?.setValue('');
    this.form.get('otroMunicipioExpedicion')?.setValue('');
    this.getMunicipios();
  }

  uploadHandler(event: FileUploadHandlerEvent, fileUpload: FileUpload) {
    const file = event.files[0];
    this.invalidSize = false;
    //validate file size
    if (file.size > (1024 * 1024)) {
      this.invalidSize = true;
      fileUpload.clear();
      return;
    }
    this.archivoCedula = file;
    fileUpload.clear();
    this.requieredFile = false;
  }

  removeFile() {
    this.archivoCedula = undefined;
    this.invalidSize = false;
  }

  onSubmit() {
    const missingFile = this.archivoCedula === undefined;
    if (this.form.invalid || missingFile) {
      Utils.markAllFormControlsAsTouched(this.form);
      if (missingFile) {
        this.requieredFile = true;
      }
      this._ms.add({ severity: 'warn', summary: this._translations.warnSummary, detail: this._translations.completeCampos });
      return;
    }

    const request = {
      tipoIdentificacionId: this.form.get('tipoIdentificacionId')?.value,
      identificacion: this.form.get('identificacion')?.value,
      primerNombre: this.form.get('primerNombre')?.value,
      segundoNombre: this.form.get('segundoNombre')?.value,
      primerApellido: this.form.get('primerApellido')?.value,
      segundoApellido: this.form.get('segundoApellido')?.value,
      correoElectronico: this.form.get('correoElectronico')?.value,
      telefono: this.form.get('telefono')?.value,
      direccion: this.form.get('direccion')?.value,
      fechaExpedicion: this.form.get('fechaExpedicion')?.value,
      paisExpedicionId: this.form.get('paisExpedicionId')?.value.id,
      departamentoExpedicionId: this.form.get('departamentoExpedicionId')?.value?.id,
      municipioExpedicionId: this.form.get('municipioExpedicionId')?.value?.id,
      otroPaisExpedicion: this.form.get('otroPaisExpedicion')?.value,
      otroDepartamentoExpedicion: this.form.get('otroDepartamentoExpedicion')?.value,
      otroMunicipioExpedicion: this.form.get('otroMunicipioExpedicion')?.value,
    } as RegistrarUsuarioRequest;

    this.registrarUsuario(request);
  }

  onLimpiarDatos() {
    this.form.reset();
    this.removeFile();
  }

  onBack() {
    this._am.onOpenTab(0);
  }
}
